/**
 * ShowMoreButton — toggle button for Anyday per-group truncation.
 *
 * Two states:
 * - Collapsed (default): "Show {N} more" → expands to show all
 * - Expanded: "Show top {N}" → collapses back to initial count
 *
 * Matches v1 visual style: eye icon + primary-colored text, font-weight 600.
 */

import { memo } from "react"
import { useTranslation } from "react-i18next"
import EyeIcon from "@/shared/icons/totodoicons/stroke/eye.svg?react"
import EyeOffIcon from "@/shared/icons/pikaicons/stroke/security/eye-off.svg?react"

interface ShowMoreButtonProps {
  /** Number shown in the label (hidden count when expanding, initial count when collapsing) */
  count: number
  onClick: () => void
  /** "expand" (default) = "Show {N} more", "collapse" = "Show top {N}" */
  variant?: "expand" | "collapse"
}

export const ShowMoreButton = memo(function ShowMoreButton({
  count,
  onClick,
  variant = "expand",
}: ShowMoreButtonProps) {
  if (count <= 0) return null

  const { t } = useTranslation()
  const isCollapse = variant === "collapse"
  const Icon = isCollapse ? EyeOffIcon : EyeIcon
  const label = isCollapse
    ? t("Show top {{count}}", { count })
    : t("Show {{count}} more", { count })

  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2.5 flex cursor-pointer items-center gap-1.5 px-2.5 text-primary"
    >
      <Icon className="size-[18px]" />
      <span className="text-[15px] font-semibold">{label}</span>
    </button>
  )
})
